import html2canvas from 'html2canvas';

// Helper function to check if an element contains text
const isTextElement = (element: HTMLElement): boolean => {
  const hasTextNode = Array.from(element.childNodes).some(
    node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim() !== ''
  );
  return hasTextNode || element.tagName === 'P' || /^H[1-6]$/.test(element.tagName);
};

// Main download function
export const downloadChart = async (element: HTMLElement, filename: string) => {
  console.log('Starting chart download process...', { filename });

  // Wait for fonts to be fully loaded and rendered
  if (document.fonts) {
    await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 200)); // Extra buffer
  }

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: true,
      useCORS: true,
      onclone: async (clonedDoc: Document) => {
        console.log('[onclone] Started.');
        const clonedElement = clonedDoc.body.querySelector('article') as HTMLElement;
        if (!clonedElement) {
          console.error('[onclone] Could not find cloned article element.');
          return;
        }

        // --- 1. HIDE ACTION BUTTONS ---
        const actionButtons = clonedElement.querySelector('.chart-action-buttons');
        if (actionButtons instanceof HTMLElement) {
          actionButtons.style.display = 'none';
        }

        // --- 2. APPLY LIVE BAR LABEL POSITIONS ---
        const originalBarLabels = element.querySelectorAll('.download-chart-label');
        const clonedBarLabels = clonedElement.querySelectorAll('.download-chart-label');

        if (originalBarLabels.length === clonedBarLabels.length) {
          originalBarLabels.forEach((originalLabel, index) => {
            if (originalLabel instanceof HTMLElement) {
              const clonedLabel = clonedBarLabels[index] as HTMLElement;
              const computedStyle = getComputedStyle(originalLabel);
              clonedLabel.style.position = 'absolute';
              clonedLabel.style.left = computedStyle.left;
              clonedLabel.style.transform = computedStyle.transform;
              clonedLabel.style.textAlign = computedStyle.textAlign;
              clonedLabel.style.opacity = computedStyle.opacity;
              clonedLabel.style.overflow = 'visible';
              clonedLabel.style.whiteSpace = 'nowrap';
              clonedLabel.style.transition = 'none';
              clonedLabel.style.zIndex = '20';
            }
          });
        }

        // --- 3. APPLY LIVE TICK LABEL POSITIONS (FINAL, ROBUST FIX) ---
        const originalChartArea = element.querySelector('.chart-area-container');
        const originalTickLabels = element.querySelectorAll('.chart-tick-label');
        const clonedTickLabels = clonedElement.querySelectorAll('.chart-tick-label');

        if (originalChartArea && originalTickLabels.length === clonedTickLabels.length) {
          const chartAreaRect = originalChartArea.getBoundingClientRect();

          originalTickLabels.forEach((originalLabel, index) => {
              if (originalLabel instanceof HTMLElement) {
                  const clonedLabel = clonedTickLabels[index] as HTMLElement;
                  const originalLabelRect = originalLabel.getBoundingClientRect();
                  const computedStyle = getComputedStyle(originalLabel);

                  // Calculate position RELATIVE to the chart area, not the whole component
                  const relativeTop = originalLabelRect.top - chartAreaRect.top;
                  
                  clonedLabel.style.position = 'absolute';
                  clonedLabel.style.top = `${relativeTop - 20}px`; // Use the robust relative position
                  clonedLabel.style.left = computedStyle.left;
                  clonedLabel.style.transform = computedStyle.transform;
                  
                  // Clean up and ensure visibility
                  clonedLabel.style.bottom = 'auto';
                  clonedLabel.style.margin = '0';
                  clonedLabel.style.padding = '0';
                  clonedLabel.style.opacity = computedStyle.opacity;
                  clonedLabel.style.whiteSpace = 'nowrap';
                  clonedLabel.style.transition = 'none';
                  clonedLabel.style.zIndex = '20';
              }
          });
        }

        // --- 4. APPLY DONUT LEGEND SPECIFIC FIXES ---
        const donutLegendItems = clonedElement.querySelectorAll('.donut-legend-item');
        donutLegendItems.forEach(item => {
          if (item instanceof HTMLElement) {
            // Ensure flexbox properties are preserved for donut legend items
            item.style.display = 'flex';
            item.style.alignItems = 'center';
            item.style.gap = '20px'; // gap-5 = 20px
            
            // Ensure inner flex container properties are preserved
            const innerContainer = item.querySelector('div.self-stretch.flex.items-center');
            if (innerContainer instanceof HTMLElement) {
              innerContainer.style.display = 'flex';
              innerContainer.style.alignItems = 'center';
              innerContainer.style.gap = '10px'; // gap-2.5 = 10px
            }
          }
        });

        // --- 5. APPLY GENERIC TEXT SHIFTING AND OTHER FIXES ---
        const descendants = clonedElement.getElementsByTagName('*');
        Array.from(descendants).forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.transition = 'none';
            el.style.animation = 'none';

            // Apply upward shift to text, BUT EXCLUDE all labels we handled manually
            const isManuallyPositioned = el.classList.contains('download-chart-label') || 
                                         el.classList.contains('chart-tick-label') ||
                                         el.classList.contains('chart-xaxis-label') ||
                                         el.classList.contains('donut-legend-text') ||
                                         el.classList.contains('donut-legend-item');

            if (isTextElement(el) && !isManuallyPositioned) {
                el.style.position = 'relative';
                el.style.top = '-8px';
                el.style.lineHeight = '1.2';
                el.style.verticalAlign = 'middle';
            }
          }
        });
        
        console.log('[onclone] Finished processing elements.');
      },
    });

    // Convert to PNG and download
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    console.log('Download link created and clicked.');

  } catch (error) {
    console.error('Error during html2canvas operation:', error);
  }
};

export const downloadChartSection = async (
  sectionElement: HTMLElement,
  filename: string
): Promise<void> => {
  console.log('Starting chart section download...');
  await downloadChart(sectionElement, filename);
};